import {
  AGENT_ROLES,
  BOARDS_ID,
  FILTER_OPERATOR_ENUMS,
  FILTER_RULE_ENUMS,
  LIMIT,
} from '@constants';
import { formatDate, getCleanedUrlParams, sortArray } from '@utils';
import { URLS } from 'layouts/DrawerLayout/constants';
import { groupBy, isNumber, omit, pick } from 'lodash-es';
import {
  TRAFFIC_OFFENSES_REASONS,
  TRAFFIC_OFFENSES_STATUSES,
} from 'components/trafficOffenses/constants';
import { TRAFFIC_OFFENSES_BOARD } from './constants';

export const getTrafficOffensesQueryParams = (payload = {}) => {
  const customFields = [];

  if (!payload?.pageSize) {
    payload.pageSize = LIMIT;
  }
  if (!payload?.page) {
    payload.page = '1';
  }

  if (!payload.order) {
    payload.order = 'desc';
    payload.orderBy = 'createdAt';
  }

  Object.keys(payload)?.forEach((key) => {
    if (customFields.includes(key)) {
      payload[`customFields.${key}`] = payload[key];
      delete payload[key];
    }
  });

  return getCleanedUrlParams(
    pick(payload, ['page', 'pageSize', 'order', 'orderBy', 'query'])
  );
};

export const getTrafficOffensesPayload = (fields = {}) => {
  const value = [
    {
      field: 'board_id',
      operator: FILTER_OPERATOR_ENUMS['='],
      type: FILTER_RULE_ENUMS.ComparableRuleType,
      value: BOARDS_ID.TRAFFIC_OFFENSES,
    },
  ];
  if (fields.assignee) {
    value.push({
      field: 'assignee',
      operator: 0,
      type: fields.assignee === 'none' ? FILTER_RULE_ENUMS.isEmpty : 5,
      value: fields.assignee === 'none' ? '' : fields.assignee,
    });
  }

  return {
    field: null,
    operator: null,
    type: FILTER_RULE_ENUMS.AndRuleType,
    value,
  };
};

export const normaliseAssigneesData = (data = []) =>
  data
    .map((item) => ({
      isActive: item?.isActive,
      value: item.email,
      text:
        item.firstName && item.lastName
          ? `${item?.firstName} ${item?.lastName}`
          : item.email,
      shortName:
        item.firstName && item.lastName
          ? `${item?.firstName[0].toUpperCase()}${item?.lastName[0].toUpperCase()}`
          : item.email[0].toUpperCase(),
    }))
    ?.sort((a, b) => a.text.localeCompare(b.text));

export const filterTrafficOffensesAssignees = (assignees = []) => {
  const allowedRoles = [
    AGENT_ROLES.isCustomerSuccessCoordinator,
    AGENT_ROLES.isCustomerSuccessTeamLead,
    AGENT_ROLES.isCustomerSuccessManager,
    AGENT_ROLES.isBillingAssociate,
  ];

  return assignees.filter(
    (item) => item?.role && allowedRoles.includes(item.role)
  );
};

export const normaliseTrafficOffensesTasks = (data = [], assignees = []) => {
  if (!data.length > 0) {
    return {};
  }

  const tasks = data.map((item) => {
    const assignee = assignees.find((a) => a.value === item.assignee) || {};

    return {
      ...item,
      id: item?.id,
      assignee,
      assigneeColumn: assignee?.text || '-',
      plate: item.customFields?.vehiclePlate || '',
      description: `${item.name}`,
      href: `${URLS.trafficOffenses}/${item?.id}`,
      userId: item.customFields.userId,
      userFullName: `${item.customFields.userFirstName || ''} ${
        item.customFields.userLastName || ''
      }`,
      carDescr: item?.name || '-',
      title: item.customFields?.vehiclePlate || '',
      createdAt: item?.createdAt
        ? formatDate(item?.createdAt, 'dd/MM/yyyy')
        : '-',
      date: item.createdAt ? formatDate(item.createdAt, 'dd/MM/yyyy') : null,
      status: item.status,
      columnStatus: TRAFFIC_OFFENSES_STATUSES[item.customFields.status] || '-',
      reason: TRAFFIC_OFFENSES_REASONS[item.customFields?.reason] || '-',
      dateOfViolation: item.customFields?.dateOfViolation
        ? formatDate(item.customFields?.dateOfViolation, 'dd/MM/yyyy')
        : '-',
      violationNumber: item.customFields.violationNumber || '-',
    };
  });

  return {
    tasks: tasks?.sort((a, b) => {
      return b.id.localeCompare(a.id);
    }),
  };
};

export const getColumns = (tasks = []) => {
  const order = TRAFFIC_OFFENSES_BOARD.statusOrder;
  const groupedByStatus = groupBy(tasks, 'status');

  const columns = order.reduce((acc, cur) => {
    acc.push({
      id: cur,
      title: TRAFFIC_OFFENSES_BOARD?.statuses?.[cur]?.label || cur,
      cards: groupedByStatus[cur] || [],
      styles: TRAFFIC_OFFENSES_BOARD?.statuses?.[cur]?.styles,
      value: true,
      label: TRAFFIC_OFFENSES_BOARD?.statuses?.[cur]?.label || cur,
      key: cur,
    });

    return acc;
  }, []);

  return columns;
};

export const handleVehicleData = (data) => {
  if (!data) {
    return {};
  }
  const properties = [];

  if (data.properties?.fuelType) {
    properties.push(data.properties.fuelType);
  }
  if (data.properties?.transmission) {
    properties.push(data.properties.transmission);
  }
  if (data.properties?.color) {
    properties.push(data.properties.color);
  }
  if (data.year) {
    properties.push(data.year);
  }

  return {
    internalId: data.internalId,
    id: data.id,
    title: [data.manufacturer, data.model].filter(Boolean).join(', '),
    plate: data.plate,
    image: data?.images?.[0]?.url,
    properties,
    vin: data.vin,
    procurement: data.procurement,
  };
};
export const handleChangeLogData = (data = []) => {
  if (!data.length) {
    return [];
  }

  return data.toReversed().map((entry) => {
    let { newValue, fieldName } = entry;

    if (fieldName === 'reason')
      newValue = TRAFFIC_OFFENSES_REASONS[entry?.newValue] || entry?.newValue;
    // back-end uses the same fieldName 'status' both for customFields changes and
    // for general task status changes. This is a temporary fix to distinguish them
    if (entry?.fieldName === 'status' && !isNumber(entry?.newValue)) {
      newValue = TRAFFIC_OFFENSES_STATUSES[entry?.newValue] || entry?.newValue;
      fieldName = 'stage';
    }

    return {
      ...entry,
      fieldName,
      newValue,
    };
  });
};
export const handleTrafficOffensesTaskData = (
  data = {},
  assignees = [],
  stages = []
) => {
  if (!data) {
    return {};
  }
  const task = {
    ...data,
    status: stages.find((item) => item?.value === data?.status),
    assignee: assignees.find((item) => item?.value === data?.assignee),
    customFields: {
      ...data.customFields,
      reason: data.customFields?.reason || '-',
      totalChargeClientAmount:
        data.customFields?.totalChargeClientAmount?.number || null,
      totalInvoiceCost: data.customFields?.totalInvoiceCost?.number || null,
      violationFee: data.customFields?.violationFee?.number || null,
    },
    changelog: handleChangeLogData(data?.changelog || []),
    comments: data?.comments
      ? sortArray(data?.comments, 'createdAt', 'desc')?.map((entry) => {
          return {
            id: entry?.id,
            date: entry?.createdAt
              ? formatDate(entry?.createdAt, 'dd MMMM yyyy')
              : '-',
            time: entry?.createdAt
              ? formatDate(entry?.createdAt, 'HH:mm')
              : '-',
            name: entry?.userFullName || entry?.user || '-',
            comment: entry?.comment,
            edit: entry?.editedAt
              ? formatDate(entry?.editedAt, 'dd MMMM yyyy HH:mm')
              : '',
          };
        })
      : [''],
    documents: data?.documents?.filter(
      (item) => !item?.tags?.includes('chargeEmailPhotosAndDocs')
    ),
    chargeEmailPhotosAndDocs: data?.documents?.filter((item) =>
      item?.tags?.includes('chargeEmailPhotosAndDocs')
    ),
  };
  return task;
};

export const getTrafficOffensesParamsAndPayload = ({
  params,
  assignee,
  query,
}) => {
  const payload = getTrafficOffensesPayload({
    assignee,
    query,
  });

  const cleanParams = getTrafficOffensesQueryParams(params);

  return { cleanParams, payload };
};

export const getTrafficOffensesTaskPayload = (values = {}) => {
  Object.keys(values.customFields)?.forEach((key) => {
    if (values.customFields[key] === '') {
      values.customFields[key] = null;
    }
  });

  const cleanedValues = omit(values, [
    'assignee',
    'documents',
    'tags',
    'status',
    'updatedAt',
    'updatedBy',
    'changelog',
    'images',
    'parent',
    'name',
    'createdAt',
    'comments',
    'createdBy',
    'subscriptionId',
    'customFields.vehicleDescription',
    'customFields.vehicleImage',
    'customFields.vehicleOwner',
    'customFields.userEmail',
    'customFields.userFirstName',
    'customFields.userId',
    'customFields.userLastName',
    'customFields.vehiclePlate',
    'customFields.vehicleVin',
    'customFields.workOrderId',
    'customFields.sendEmailDocs',
    'customFields.sendEmailPhotos',
    'customFields.sendEmailTemplate',
    'customFields.carId',
  ]);

  return {
    assignee: values.assignee?.value,
    customFields: {
      ...cleanedValues.customFields,
    },
  };
};

export const getTrafficOffensesExportPayload = () => {
  return {
    type: FILTER_RULE_ENUMS.AndRuleType,
    field: null,
    operator: null,
    value: [
      {
        type: FILTER_RULE_ENUMS.ComparableRuleType,
        field: 'boardId',
        operator: 0,
        value: BOARDS_ID.TRAFFIC_OFFENSES,
      },
    ],
  };
};
